import { Link } from "react-router-dom";
import { ClipboardCheck, Layers, Lock, Ruler, ShieldCheck, Thermometer, Wrench } from "lucide-react";
import { COMPARE_CHECKS, COMPARE_HOW } from "../../data/compareContent";
import { FaqItems } from "../faq/FaqItems";
import { FaqMoreLink } from "../product/ProductFAQ";
import { PhoneLink, QuoteButton, SectionHeading, WhatsAppButton } from "../home/CtaButtons";

const ICONS = { Layers, Thermometer, Wrench, Ruler, ShieldCheck, ClipboardCheck };

export const CompareAside = () => (
  <aside className="space-y-5 lg:col-span-4 lg:pt-2" data-testid="compare-aside">
    <div className="card p-6">
      <h2 className="text-[17px] font-semibold text-ink">Uw offerte wordt persoonlijk beoordeeld.</h2>
      <p className="mt-3 text-[14.5px] leading-relaxed text-slate-600">Na ontvangst bekijken wij uw bestaande offerte, de productspecificaties en de gewenste werkzaamheden.</p>
      <p className="mt-3 text-[14.5px] leading-relaxed text-slate-600">Wij nemen zo spoedig mogelijk persoonlijk contact met u op om de mogelijkheden te bespreken.</p>
    </div>
    <div className="card p-6" data-testid="compare-privacy-card">
      <h2 className="flex items-center gap-2 text-[17px] font-semibold text-ink"><Lock className="h-4 w-4 text-maas" aria-hidden="true" /> Veilig en vertrouwelijk</h2>
      <p className="mt-3 text-[14.5px] leading-relaxed text-slate-600">Uw documenten worden beveiligd opgeslagen en zijn alleen toegankelijk voor ons team. Wij delen uw offerte niet met andere bedrijven.
        {" "}<Link to="/privacy" className="font-medium text-maas underline underline-offset-2">Privacyverklaring</Link></p>
    </div>
    <div className="card p-6">
      <h2 className="text-[17px] font-semibold text-ink">Liever direct contact?</h2>
      <p className="mt-2 text-[14.5px] text-slate-600">Stuur uw offerte of een foto via WhatsApp, of bel ons tijdens kantooruren.</p>
      <div className="mt-4 flex flex-col items-start gap-3">
        <WhatsAppButton location="compare-aside" className="w-full" />
        <PhoneLink location="compare-aside" />
      </div>
    </div>
  </aside>
);

export const CompareChecks = () => (
  <section aria-labelledby="checks-title" className="section" data-testid="compare-checks">
    <div className="container-tight">
      <SectionHeading id="checks-title" eyebrow="Waar letten wij op?" title="Een offerte is meer dan een totaalbedrag"
        lead="Bij het beoordelen van uw offerte kijken we naar alles wat de prijs en de kwaliteit bepaalt. Zo ziet u wat u werkelijk krijgt." />
      <ul className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {COMPARE_CHECKS.map((c) => {
          const Icon = ICONS[c.icon];
          return (
            <li key={c.title} className="card card-hover p-6">
              <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-maas-soft text-maas"><Icon className="h-5 w-5" aria-hidden="true" /></span>
              <h3 className="mt-4 text-[17px] font-semibold text-ink">{c.title}</h3>
              <p className="mt-2 text-[14.5px] leading-relaxed text-slate-600">{c.text}</p>
            </li>
          );
        })}
      </ul>
    </div>
  </section>
);

export const CompareWhy = () => (
  <section aria-labelledby="why-compare-title" className="section-tight bg-slate-50" data-testid="compare-why">
    <div className="container-prose prose-kozijn">
      <h2 id="why-compare-title">Waarom een tweede blik op uw offerte loont</h2>
      <p>Nieuwe kozijnen zijn een investering voor tientallen jaren. Toch zijn offertes lastig te vergelijken: het ene bedrijf noemt een totaalprijs, het andere werkt met losse posten en weer een ander vermeldt het profielsysteem of de glassoort niet. Daardoor lijkt een offerte soms goedkoper, terwijl er onderdelen ontbreken.</p>
      <p>Wij bekijken uw offerte met de ogen van een vakman. Staan de <Link to="/montage">montage en afwerking</Link> erin? Welk <Link to="/hr++-glas-isolatieglas">glas</Link> wordt er geleverd? Gaat het om <Link to="/kunststof-kozijnen">kunststof kozijnen</Link> of <Link to="/aluminium-kozijnen">aluminium kozijnen</Link>, en past het profiel bij uw woning? Daarna laten we u eerlijk weten wat wij voor u kunnen betekenen.</p>
      <p>Wilt u eerst een indruk van de kosten? Lees dan onze uitleg over de <Link to="/kosten-kunststof-kozijnen-zuid-holland">kosten van nieuwe kozijnen</Link> in Zuid-Holland.</p>
    </div>
  </section>
);

export const CompareHow = () => (
  <section aria-labelledby="how-title" className="section" data-testid="compare-how">
    <div className="container-tight">
      <SectionHeading id="how-title" eyebrow="Zo werkt het" title="In vier stappen uw offerte laten beoordelen" />
      <ol className="mt-10 grid gap-5 md:grid-cols-4">
        {COMPARE_HOW.map((s, i) => (
          <li key={s.title} className="rounded-2xl border border-slate-200 bg-white p-5">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-maas-soft font-display text-[15px] font-bold text-maas">{i + 1}</span>
            <h3 className="mt-4 text-[16.5px] font-semibold text-ink">{s.title}</h3>
            <p className="mt-1.5 text-[14.5px] leading-relaxed text-slate-600">{s.text}</p>
          </li>
        ))}
      </ol>
    </div>
  </section>
);

export const CompareFAQ = ({ faqs }) => (faqs?.length ? (
  <section aria-labelledby="compare-faq-title" className="section-tight" data-testid="compare-faq">
    <div className="container-tight grid gap-10 lg:grid-cols-12 lg:gap-14">
      <div className="lg:col-span-4">
        <SectionHeading id="compare-faq-title" eyebrow="Veelgestelde vragen" title="Over offerte vergelijken" />
        <div className="mt-5"><FaqMoreLink testid="compare-faq-all-link" /></div>
      </div>
      <div className="lg:col-span-8"><FaqItems faqs={faqs} testid="compare-faq-item" /></div>
    </div>
  </section>
) : null);

export const CompareEndCTA = () => (
  <section aria-labelledby="compare-cta-title" className="pb-16 sm:pb-20" data-testid="compare-end-cta">
    <div className="container-tight">
      <div className="flex flex-col gap-6 rounded-3xl border border-slate-200 bg-maas-soft px-6 py-10 sm:px-10 lg:flex-row lg:items-center lg:justify-between">
        <div className="max-w-2xl">
          <h2 id="compare-cta-title" className="h3">Nog geen offerte ontvangen?</h2>
          <p className="mt-2 text-[15.5px] leading-relaxed text-slate-600">Vraag dan direct een vrijblijvende prijsindicatie aan voor nieuwe kozijnen, een schuifpui of een deur.</p>
        </div>
        <QuoteButton location="compare-end" className="w-full sm:w-auto" />
      </div>
    </div>
  </section>
);
